import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';

export interface BackendContact {
  id: string;
  businessAccountId: string;
  name: string | null;
  phoneNumber: string;
  email: string;
  tags: any[];
  customFields: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface BulkImportContact {
  name: string;
  phoneNumber: string;
  email?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status?: string;
  notes?: string;
}

export interface Contact {
  id: string;
  tenant_id: string;
  business_account_id: string;
  name: string | null;
  phone_number: string;
  profile_picture_url: string | null;
  type?: 'LEAD' | 'CUSTOMER';
  status: string;
  custom_fields: Record<string, any>;
  tags: string[];
  last_seen: string | null;
  campaign_opted_out?: boolean;
  campaignOptedOut?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateContactData {
  businessAccountId?: string;
  workspaceId?: string;
  phoneNumber: string;
  name?: string;
  email?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status?: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
}

export interface UpdateContactData {
  name?: string;
  phoneNumber?: string;
  email?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status?: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  notes?: string;
  campaignOptedOut?: boolean;
}

export interface ContactFilters {
  id?: string;
  business_account_id?: string;
  workspace_id?: string;
  status?: string;
  search?: string;
  type?: 'LEAD' | 'CUSTOMER';
  tags?: string[];
  limit?: number;
  offset?: number;
  sort_by?: string;
  order?: 'asc' | 'desc';
  orderBy?: string;
  ascending?: boolean;
  source?: string;
  has_email?: boolean;
  has_phone?: boolean;
  created_from?: string; // ISO date or YYYY-MM-DD (inclusive)
  created_to?: string;   // ISO date or YYYY-MM-DD (inclusive end-of-day)
}

export interface PaginatedContactsResponse {
  success: boolean;
  data: BackendContact[];
  count: number;
  totalCount: number;
  filteredTotalCount?: number;
  hasMore: boolean;
}

export const contactsApi = {
  async getCustomerIds(businessAccountId: string): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>(
        `/contacts/customers?businessAccountId=${businessAccountId}`
      );

      if (response.error) {
        console.error('Error fetching customer IDs:', response.error);
        return [];
      }

      if (Array.isArray(response.data)) {
        return response.data;
      }

      console.warn('Unexpected response format for getCustomerIds:', response);
      return [];
    } catch (error) {
      console.error('Exception in getCustomerIds:', error);
      return [];
    }
  },

  async getAll(filters: ContactFilters = {}) {
    const params: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params[key] = value.join(',');
        } else {
          params[key] = String(value);
        }
      }
    });

    return apiClient.get<PaginatedContactsResponse>(API_ENDPOINTS.CONTACTS, { params });
  },

  async getById(id: string) {
    return apiClient.get<BackendContact>(`${API_ENDPOINTS.CONTACTS}/${id}`);
  },

  async create(data: CreateContactData) {
    return apiClient.post<BackendContact>(API_ENDPOINTS.CONTACTS, data);
  },

  async update(id: string, data: UpdateContactData) {
    return apiClient.put<BackendContact>(`${API_ENDPOINTS.CONTACTS}/${id}`, data);
  },

  async delete(id: string) {
    return apiClient.delete(`${API_ENDPOINTS.CONTACTS}/${id}`);
  },

  async updateFlowSettings(id: string, businessAccountId: string, isFlowsPaused: boolean) {
    return apiClient.post<{ success: boolean; data: BackendContact }>(
      `${API_ENDPOINTS.CONTACTS}/${id}/flow-settings`,
      {
        businessAccountId,
        isFlowsPaused,
      }
    );
  },

  async search(query: string, businessAccountId?: string, workspaceId?: string) {
    const filters: ContactFilters = {
      search: query,
      business_account_id: businessAccountId,
      workspace_id: workspaceId,
    };
    return this.getAll(filters);
  },

  async getByBusinessAccount(businessAccountId: string | undefined, filters: Omit<ContactFilters, 'business_account_id'> = {}) {
    return this.getAll({ ...filters, business_account_id: businessAccountId });
  },

  async bulkImport(
    businessAccountId: string | null | undefined,
    contacts: BulkImportContact[],
    fileUrl?: string,
    overwriteExisting?: boolean,
    workspaceId?: string
  ) {
    return apiClient.post(`${API_ENDPOINTS.CONTACTS}/bulk-import`, {
      businessAccountId: businessAccountId || undefined,
      workspace_id: workspaceId,
      contacts,
      fileUrl,
      overwriteExisting: overwriteExisting === true,
    });
  },

  async getUploadUrl(
    businessAccountId: string | null | undefined,
    fileName: string,
    fileType: string,
    workspaceId?: string
  ) {
    return apiClient.post<{ uploadUrl: string; fileUrl: string; key: string }>(
      `${API_ENDPOINTS.CONTACTS}/upload-url`,
      {
        businessAccountId: businessAccountId || undefined,
        workspace_id: workspaceId,
        fileName,
        fileType,
      }
    );
  },

  async getTagsByContactIds(businessAccountId: string, contactIds: string[]) {
    if (!contactIds || contactIds.length === 0) {
      return { data: { success: true, data: {} as Record<string, Array<{ id: string; name: string; color: string; description: string | null }>> }, error: null, success: true } as any;
    }
    return apiClient.get<{ success: boolean; data: Record<string, Array<{ id: string; name: string; color: string; description: string | null }>> }>(
      `${API_ENDPOINTS.CONTACTS}/tags-by-contacts`,
      {
        params: {
          business_account_id: businessAccountId,
          contact_ids: contactIds.join(','),
        },
      }
    );
  },

  async getTags(businessAccountId?: string, type?: 'LEAD' | 'CUSTOMER', workspaceId?: string) {
    return apiClient.get<{ success: boolean; data: Array<{ id: string; name: string; color: string; description: string; count: number }> }>(
      `${API_ENDPOINTS.CONTACTS}/tags`,
      { params: { business_account_id: businessAccountId, workspace_id: workspaceId, type } }
    );
  },

  /** Tags without their per-tag contact counts — for pickers and filters that never show a count. */
  async getTagList(businessAccountId?: string, workspaceId?: string) {
    return apiClient.get<{ success: boolean; data: Array<{ id: string; name: string; color: string; description: string }> }>(
      `${API_ENDPOINTS.CONTACTS}/tags`,
      { params: { business_account_id: businessAccountId, workspace_id: workspaceId, counts: 'false' } }
    );
  },

  async getTagsPaged(businessAccountId?: string, { limit = 25, offset = 0, search, type, workspaceId }: { limit?: number; offset?: number; search?: string; type?: 'LEAD' | 'CUSTOMER'; workspaceId?: string } = {}) {
    return apiClient.get<{ success: boolean; data: Array<{ id: string; name: string; color: string; description: string; count: number }> }>(
      `${API_ENDPOINTS.CONTACTS}/tags`,
      {
        params: {
          business_account_id: businessAccountId,
          workspace_id: workspaceId,
          limit,
          offset,
          search: search && search.trim().length > 0 ? search : undefined,
          type
        }
      }
    );
  },

  async createTag(businessAccountId: string | null | undefined, name: string, color: string = "#3B82F6", description?: string, contactIds: string[] = [], workspaceId?: string | null) {
    return apiClient.post(`${API_ENDPOINTS.CONTACTS}/tags`, {
      business_account_id: businessAccountId || undefined,
      workspace_id: workspaceId || undefined,
      name,
      color,
      description,
      contact_ids: contactIds,
    });
  },

  async deleteTag(
    businessAccountId: string | null | undefined,
    tagId: string,
    workspaceId?: string
  ) {
    return apiClient.delete(`${API_ENDPOINTS.CONTACTS}/tags`, {
      body: {
        business_account_id: businessAccountId || undefined,
        workspace_id: workspaceId,
        tag_id: tagId,
      },
    });
  },

  async bulkAddTagsToContacts(businessAccountId: string | null | undefined, contactIds: string[], tagIds: string[], workspaceId?: string) {
    return apiClient.post(`${API_ENDPOINTS.CONTACTS}/bulk/tags`, {
      businessAccountId: businessAccountId || undefined,
      workspace_id: workspaceId,
      contactIds,
      tagIds,
    });
  },

  async bulkRemoveTagsFromContacts(businessAccountId: string | null | undefined, contactIds: string[], tagIds: string[], workspaceId?: string) {
    return apiClient.delete(`${API_ENDPOINTS.CONTACTS}/bulk/tags`, {
      body: {
        businessAccountId: businessAccountId || undefined,
        workspace_id: workspaceId,
        contactIds,
        tagIds,
      },
    });
  },

  async updateTag(
    businessAccountId: string | null | undefined,
    tagId: string,
    name: string,
    color: string,
    description?: string,
    workspaceId?: string
  ) {
    return apiClient.put(`${API_ENDPOINTS.CONTACTS}/tags/${tagId}`, {
      business_account_id: businessAccountId || undefined,
      workspace_id: workspaceId,
      name,
      color,
      description,
    });
  },

  async block(id: string, businessAccountId: string) {
    return apiClient.post<{ success: boolean; data: BackendContact }>(
      `${API_ENDPOINTS.CONTACTS}/${id}/block`,
      {},
      { params: { businessAccountId } }
    );
  },

  async unblock(id: string, businessAccountId: string) {
    return apiClient.post<{ success: boolean; data: BackendContact }>(
      `${API_ENDPOINTS.CONTACTS}/${id}/unblock`,
      {},
      { params: { businessAccountId } }
    );
  },

  async getBlocked(businessAccountId: string) {
    return apiClient.get<{ success: boolean; data: BackendContact[]; count: number; totalCount: number }>(
      `${API_ENDPOINTS.CONTACTS}/blocked`,
      { params: { businessAccountId } }
    );
  },

  async getStats(
    businessAccountId: string,
    type?: string,
    dateRange?: { start?: string; end?: string }
  ) {
    const params: any = { business_account_id: businessAccountId };
    if (type) params.type = type;
    if (dateRange?.start) params.startDate = dateRange.start;
    if (dateRange?.end) params.endDate = dateRange.end;

    return apiClient.get<{
      success: boolean;
      data: {
        newContactsToday: number;
        newContactsInRange: number | null;
        unsubscribed: number;
        noInteractivity30Days: number;
        totalContacts: number;
      };
    }>(`${API_ENDPOINTS.CONTACTS}/stats`, { params });
  },

  async checkLimit(businessAccountId: string | null | undefined, contactsToImport: number, workspaceId?: string) {
    return apiClient.get<{
      currentCount: number;
      limit: number | null;
      contactsToImport: number;
      totalAfterImport: number;
      wouldExceed: boolean;
      allowed: boolean;
      remaining: number | null;
      message: string | null;
    }>(`${API_ENDPOINTS.CONTACTS}/check-limit`, {
      params: {
        businessAccountId: businessAccountId || undefined,
        workspace_id: workspaceId,
        contactsToImport,
      },
    });
  },

  async checkTagLimit(businessAccountId: string | null | undefined, tagNames: string[], workspaceId?: string) {
    return apiClient.get<{
      currentTagCount: number;
      limit: number | null;
      existingTags: string[];
      newTags: string[];
      newTagsCount: number;
      totalAfterImport: number;
      wouldExceed: boolean;
      allowed: boolean;
      remaining: number | null;
      message: string | null;
    }>(`${API_ENDPOINTS.CONTACTS}/check-tag-limit`, {
      params: {
        businessAccountId: businessAccountId || undefined,
        workspace_id: workspaceId,
        tagNames,
      },
    });
  },

  async checkExistingPhones(businessAccountId: string | null | undefined, phoneNumbers: string[], workspaceId?: string) {
    return apiClient.post<{
      success: boolean;
      data?: {
        existing: Array<{
          id: string;
          phone_number: string;
          name: string | null;
          email: string | null;
        }>;
      };
      error?: string;
    }>(`${API_ENDPOINTS.CONTACTS}/check-existing-phones`, {
      businessAccountId: businessAccountId || undefined,
      workspace_id: workspaceId,
      phoneNumbers,
    });
  },

  async getGrowthStats(workspaceId: string, year: number, type?: string) {
    const params: any = {
      workspace_id: workspaceId,
      year,
    };
    if (type) params.type = type;

    return apiClient.get<{
      success: boolean;
      data: number[];
    }>(`${API_ENDPOINTS.CONTACTS}/growth`, { params });
  },

  async bulkInitiateCalls(businessAccountId: string | null | undefined, contactIds: string[], workspaceId?: string) {
    return apiClient.post<{
      success: boolean;
      data: {
        initiated: number;
        failed: number;
        skipped: number;
        calls: Array<{
          contactId: string;
          contactName: string;
          phone: string;
          executionId: string;
          status: string;
        }>;
        errors?: Array<{ error: string; code?: string }>;
      };
      message: string;
    }>(`${API_ENDPOINTS.CONTACTS}/bulk/call`, {
      businessAccountId: businessAccountId || undefined,
      workspace_id: workspaceId,
      contactIds,
    });
  },

  async requestExport(body: Record<string, unknown>): Promise<{
    ok: boolean;
    jobId?: string;
    error?: string;
  }> {
    const res = await apiClient.post<{ jobId: string }>(
      `${API_ENDPOINTS.CONTACTS}/export`,
      body
    );
    if (res.error || res.success === false) {
      return { ok: false, error: res.error || 'Failed to start export' };
    }
    const jobId = (res.data as any)?.jobId || (res as any)?.jobId;
    if (!jobId) {
      return { ok: false, error: 'Export job id missing' };
    }
    return { ok: true, jobId };
  },

  async getExportStatus(jobId: string): Promise<{
    ok: boolean;
    status?: string;
    fileUrl?: string | null;
    format?: string | null;
    filename?: string | null;
    error?: string;
  }> {
    const res = await apiClient.get<{
      jobId: string;
      status: string;
      fileUrl?: string | null;
      format?: string | null;
      filename?: string | null;
      error?: string | null;
    }>(`${API_ENDPOINTS.CONTACTS}/export/${jobId}`);
    if (res.error || res.success === false) {
      return { ok: false, error: res.error || 'Failed to get export status' };
    }
    const data = (res.data || res) as any;
    return {
      ok: true,
      status: data.status,
      fileUrl: data.fileUrl ?? null,
      format: data.format ?? null,
      filename: data.filename ?? null,
      error: data.error || undefined,
    };
  },

  async exportCsv(body: Record<string, unknown>): Promise<{
    ok: boolean;
    error?: string;
    truncated?: boolean;
    jobId?: string;
  }> {
    return this.requestExport(body);
  },

};
